import moment from 'moment';
import { getCurrentlyAbnormalLabs, isValueOutOfRange } from './referenceRangeUtils';

/**
 * Determines if a lab value is abnormal based on flag or reference range
 * @param {Object} lab - Lab entry object
 * @returns {boolean} - True if abnormal, false otherwise
 */
export const isLabAbnormal = (lab) => {
  if (!lab) return false;

  // First check if there's an explicit flag indicating abnormality
  if (lab.flag) {
    const flag = lab.flag.toString().toUpperCase();
    if (['H', 'L', 'HIGH', 'LOW', 'A', 'ABNORMAL', 'C', 'CRITICAL'].includes(flag)) {
      return true;
    }
  }

  // If no abnormal flag but we have value and reference range, check against range
  if (lab.value !== null && lab.value !== undefined && lab.referenceRange) {
    const rangeCheck = isValueOutOfRange(lab.value, lab.referenceRange);
    return rangeCheck.isAbnormal;
  }

  return false;
};

/**
 * Filters lab entries to only include abnormal values
 * @param {Array} labEntries - Array of lab entry objects
 * @returns {Array} - Array of abnormal lab entries
 */
export const filterAbnormalLabs = (labEntries) => {
  if (!Array.isArray(labEntries)) return [];
  
  return labEntries.filter(lab => {
    // Must have a value to be considered
    if (!lab.value && lab.value !== 0) return false;
    
    // Must be abnormal
    return isLabAbnormal(lab);
  });
};

/**
 * Filters lab entries to only include recent values
 * @param {Array} labEntries - Array of lab entry objects
 * @param {number} daysBack - Number of days to look back (default: 30)
 * @returns {Array} - Array of recent lab entries
 */
export const filterRecentLabs = (labEntries, daysBack = 30) => {
  if (!Array.isArray(labEntries)) return [];
  
  const cutoffDate = moment().subtract(daysBack, 'days').startOf('day');
  
  return labEntries.filter(lab => {
    if (!lab.entryDate) return false;
    
    const labDate = moment(lab.entryDate);
    return labDate.isAfter(cutoffDate);
  });
};

/**
 * Gets recent abnormal lab values for a patient
 * @param {Array} labEntries - Array of all lab entries
 * @param {number} daysBack - Number of days to look back (default: 30)
 * @returns {Array} - Array of recent abnormal lab entries
 */
export const getRecentAbnormalLabs = (labEntries, daysBack = 30) => {
  if (!Array.isArray(labEntries)) return [];

  // First filter for recent labs, then filter for abnormal ones
  const recentLabs = filterRecentLabs(labEntries, daysBack);
  const abnormalLabs = filterAbnormalLabs(recentLabs);

  // Remove duplicates by parameter name, keeping the most recent
  const uniqueAbnormalLabs = [];
  const seenParameters = new Set();

  // Sort by date descending first
  const sortedLabs = abnormalLabs.sort((a, b) =>
    new Date(b.entryDate) - new Date(a.entryDate)
  );

  for (const lab of sortedLabs) {
    if (!seenParameters.has(lab.name)) {
      uniqueAbnormalLabs.push(lab);
      seenParameters.add(lab.name);
    }
  }

  return uniqueAbnormalLabs;
};

/**
 * Gets most recent values for parameters that have had abnormal values recently
 * This shows the current status (which may be normal now) for parameters that were recently abnormal
 * @param {Array} labEntries - Array of all lab entries
 * @param {number} daysBack - Number of days to look back for abnormal values (default: 30)
 * @returns {Array} - Array of most recent lab entries for parameters with recent abnormal history
 */
export const getMostRecentLabsWithAbnormalHistory = (labEntries, daysBack = 30) => {
  if (!Array.isArray(labEntries)) return [];

  // Get parameters that have had abnormal values recently
  const recentAbnormalLabs = getRecentAbnormalLabs(labEntries, daysBack);
  const abnormalParameterNames = new Set(recentAbnormalLabs.map(lab => lab.name));

  if (abnormalParameterNames.size === 0) return [];

  // Get the most recent value for each parameter that has had abnormal values
  const mostRecentValues = [];
  const sortedAllLabs = labEntries.sort((a, b) =>
    new Date(b.entryDate) - new Date(a.entryDate)
  );

  for (const parameterName of abnormalParameterNames) {
    // Find the most recent entry for this parameter
    const mostRecentEntry = sortedAllLabs.find(lab => lab.name === parameterName);
    if (mostRecentEntry) {
      mostRecentValues.push({
        ...mostRecentEntry,
        hasRecentAbnormalHistory: true // Flag to indicate this parameter had recent abnormal values
      });
    }
  }

  // Sort by date descending
  return mostRecentValues.sort((a, b) =>
    new Date(b.entryDate) - new Date(a.entryDate)
  );
};

/**
 * Transforms flowsheet lab entries to show currently abnormal values
 * @param {Array} flowsheetEntries - Array of flowsheet entries
 * @param {number} daysBack - Number of days to look back (unused, kept for compatibility)
 * @returns {Array} - Array of formatted lab entries that are currently out of range
 */
export const transformLabsForAbnormalDisplay = (flowsheetEntries, daysBack = 30) => {
  if (!Array.isArray(flowsheetEntries)) return [];

  // Filter for lab entries only
  const labEntries = flowsheetEntries.filter(entry =>
    entry.type === 'Lab' ||
    (entry.name && entry.name.startsWith('labs_')) ||
    entry.category === 'Lab Results'
  );

  // Get currently abnormal labs (most recent values that are out of range)
  const currentlyAbnormalLabs = getCurrentlyAbnormalLabs(labEntries);

  // Transform to expected format
  return currentlyAbnormalLabs.map(lab => ({
    name: lab.name,
    value: lab.value,
    units: lab.units || '',
    referenceRange: lab.referenceRange || '',
    flag: lab.computedFlag || lab.flag || '', // Use computed flag from range analysis
    entryDate: lab.entryDate,
    category: lab.category || 'Lab',
    source: lab.source || 'Lab',
    isCurrentlyAbnormal: lab.isCurrentlyAbnormal || false,
    abnormalReason: lab.abnormalReason || '',
    abnormalDetails: lab.abnormalDetails || ''
  }));
};

/**
 * Groups abnormal labs by category for organized display
 * @param {Array} abnormalLabs - Array of abnormal lab entries
 * @returns {Object} - Object with categories as keys and lab arrays as values
 */
export const groupAbnormalLabsByCategory = (abnormalLabs) => {
  if (!Array.isArray(abnormalLabs)) return {};
  
  const grouped = {};
  
  abnormalLabs.forEach(lab => {
    // Determine category from parameter name if not explicitly set
    let category = lab.category || 'Other';
    
    if (lab.name.includes('lipid')) {
      category = 'Lipid Panel';
    } else if (lab.name.includes('cbc')) {
      category = 'Complete Blood Count';
    } else if (lab.name.includes('cmp')) {
      category = 'Comprehensive Metabolic Panel';
    } else if (lab.name.includes('glucose') || lab.name.includes('hba1c')) {
      category = 'Glucose/Diabetes';
    } else if (lab.name.includes('liver')) {
      category = 'Liver Function';
    } else if (lab.name.includes('thyroid')) {
      category = 'Thyroid Function';
    }
    
    if (!grouped[category]) {
      grouped[category] = [];
    }
    
    grouped[category].push(lab);
  });
  
  return grouped;
};

/**
 * Gets historical data for a specific lab parameter
 * @param {Array} labEntries - Array of all lab entries
 * @param {string} parameterName - Name of the lab parameter
 * @param {number} daysBack - Number of days to look back (default: 365 for 1 year)
 * @returns {Array} - Array of historical lab entries for the parameter, sorted by date descending
 */
export const getLabParameterHistory = (labEntries, parameterName, daysBack = 365) => {
  if (!Array.isArray(labEntries) || !parameterName) return [];

  const cutoffDate = moment().subtract(daysBack, 'days').startOf('day');

  return labEntries
    .filter(lab => {
      // Filter by parameter name and date
      if (lab.name !== parameterName) return false;
      if (!lab.entryDate) return false;

      const labDate = moment(lab.entryDate);
      return labDate.isAfter(cutoffDate);
    })
    .sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate)) // Most recent first
    .map(lab => ({
      name: lab.name,
      value: lab.value,
      units: lab.units || '',
      referenceRange: lab.referenceRange || '',
      flag: lab.flag || '',
      entryDate: lab.entryDate,
      category: lab.category || 'Lab',
      source: lab.source || 'Lab',
      isAbnormal: isLabAbnormal(lab)
    }));
};

/**
 * Gets a summary count of abnormal labs by severity
 * @param {Array} abnormalLabs - Array of abnormal lab entries
 * @returns {Object} - Object with counts by severity level
 */
export const getAbnormalLabsSummary = (abnormalLabs) => {
  if (!Array.isArray(abnormalLabs)) {
    return { total: 0, critical: 0, high: 0, low: 0, abnormal: 0 };
  }

  const summary = { total: abnormalLabs.length, critical: 0, high: 0, low: 0, abnormal: 0 };

  abnormalLabs.forEach(lab => {
    if (!lab.flag) return;

    const flag = lab.flag.toString().toUpperCase();
    switch (flag) {
      case 'C':
      case 'CRITICAL':
        summary.critical++;
        break;
      case 'H':
      case 'HIGH':
        summary.high++;
        break;
      case 'L':
      case 'LOW':
        summary.low++;
        break;
      case 'A':
      case 'ABNORMAL':
        summary.abnormal++;
        break;
    }
  });

  return summary;
};
